







































































































import { Component, Prop, Vue } from "vue-property-decorator";

const DRAGGING_MARGIN_X = 10;
const DRAGGING_MARGIN_Y = 10;

const ASPECT_RATIO = 16 / 10;
const MIN_WIDTH = 300;
const MIN_HEIGHT = MIN_WIDTH / ASPECT_RATIO;

type Listeners = { [key: string]: EventListener };

enum MouseActionType {
  DRAG = "drag",
  RESIZE = "resize",
}

type MouseActionState = {
  active: boolean;
  offset: { x: number; y: number };
  listeners: Listeners;
  type: MouseActionType;
};

enum ResizeDirection {
  WEST = "resize-w",
  EAST = "resize-e",
  SOUTH = "resize-s",
  SOUTH_WEST = "resize-sw",
  SOUTH_EAST = "resize-se",
}

let nextStreamId = 1;

@Component
export default class StreamIframe extends Vue {
  $enums = {
    ResizeDirection,
  };

  $refs!: {
    "stream-container": HTMLElement;
    "iframe-container": HTMLElement;
  };
  @Prop({ required: true }) url!: string;

  showIframe = true;
  streamId = "";
  streamWindow: Window | null = null;

  x = DRAGGING_MARGIN_X;
  y = DRAGGING_MARGIN_Y;
  width = MIN_WIDTH;

  collapsed = false;
  transitioning = false;
  loading = true;

  dragState: MouseActionState = {
    active: false,
    offset: { x: 0, y: 0 },
    listeners: {},
    type: MouseActionType.DRAG,
  };

  resizeState: MouseActionState = {
    active: false,
    offset: { x: 0, y: 0 },
    listeners: {},
    type: MouseActionType.RESIZE,
  };

  resizeDirection: ResizeDirection = ResizeDirection.SOUTH;

  mounted(): void {
    this.width = window.innerWidth * 0.33;

    window.addEventListener("resize", this.onWindowResize);

    this.streamId = "streaming-iframe-" + nextStreamId;
    nextStreamId += 1;
  }

  beforeDestroy(): void {
    window.removeEventListener("resize", this.onWindowResize);
    StreamIframe.removeAllMouseActionListeners(this.dragState);
    StreamIframe.removeAllMouseActionListeners(this.resizeState);
  }

  onWindowResize(): void {
    this.updatePosition(this.x, this.y, 0, 0);
    this.$nextTick(() =>
      this.updateSize(ResizeDirection.EAST, this.x + this.width, this.y)
    );
  }

  get height(): number {
    return this.width / ASPECT_RATIO;
  }

  onLoad(): void {
    this.loading = false;
  }

  refreshStream(event: Event): void {
    event.preventDefault();

    if (this.streamWindow) {
      this.streamWindow.location.href = this.url;
    } else {
      this.loading = true;
      this.showIframe = false;
      this.$nextTick(() => (this.showIframe = true));
    }
  }

  openInPopup(event: Event): void {
    event.preventDefault();

    if (this.streamWindow) {
      this.showIframe = false;
      this.collapsed = true;

      this.streamWindow.focus();

      return;
    }

    const win = window.open(
      this.url,
      this.streamId,
      `resizable,innerWidth=${this.width},innherHeight=${this.height}`
    );

    if (win) {
      this.showIframe = false;
      this.collapsed = true;

      win.addEventListener("beforeunload", this.closeStreamWindow);
      this.streamWindow = win;
    }
  }

  closeStreamWindow(): void {
    if (this.streamWindow) {
      this.streamWindow.close();
    }

    this.streamWindow = null;
    this.showIframe = true;
    this.collapsed = false;
    this.loading = true;
  }

  toggleCollapsed(event: Event): void {
    event.preventDefault();

    if (this.streamWindow) {
      this.closeStreamWindow();
      return;
    }

    this.collapsed = !this.collapsed;
    if (this.collapsed) {
      // Check if container height still fits.
    }
  }

  updateTransitioning(transitioning: boolean): void {
    this.transitioning = transitioning;
  }

  startDragging(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof HTMLElement && !target.classList.contains("icon")) {
      this.startMouseAction(this.dragState, event);
    }
  }

  stopDragging(event: MouseEvent): void {
    this.stopMouseAction(this.dragState, event);
  }

  startResizing(event: MouseEvent, direction: ResizeDirection): void {
    this.resizeDirection = direction;
    this.startMouseAction(this.resizeState, event);
  }

  startMouseAction(state: MouseActionState, event: MouseEvent): void {
    event.preventDefault();

    state.offset.x = event.clientX - this.x;
    state.offset.y = event.clientY - this.y;

    state.active = true;

    this.updateIframesPointerEvents();

    StreamIframe.addMouseActionListener(state, "mouseup", (event: Event) => {
      this.stopMouseAction(state, event);
    });

    StreamIframe.addMouseActionListener(state, "mousemove", (event: Event) => {
      this.updateMouseAction(state, event);
    });
  }

  stopMouseAction(state: MouseActionState, event: Event): void {
    event.preventDefault();

    state.active = false;
    this.updateIframesPointerEvents();
    StreamIframe.removeAllMouseActionListeners(state);
  }

  updateMouseAction(state: MouseActionState, event: Event): void {
    event.preventDefault();
    if (!(event instanceof MouseEvent)) {
      throw new Error("Unexpected event: " + event);
    }

    const type = state.type;

    switch (type) {
      case MouseActionType.DRAG:
        return this.updatePosition(
          event.clientX,
          event.clientY,
          state.offset.x,
          state.offset.y
        );

      case MouseActionType.RESIZE:
        return this.updateSize(
          this.resizeDirection,
          event.clientX,
          event.clientY
        );
    }

    function checkExhaustive(type: never): void {
      throw new Error("Unhandled type (add to switch above): " + type);
    }

    return checkExhaustive(type);
  }

  static addMouseActionListener(
    state: MouseActionState,
    name: string,
    listener: EventListener
  ): void {
    if (state.listeners[name]) {
      StreamIframe.removeMouseActionListener(state, name);
    }
    document.addEventListener(name, listener);
    state.listeners[name] = listener;
  }

  static removeAllMouseActionListeners(state: MouseActionState): void {
    for (const name of Object.keys(state.listeners)) {
      StreamIframe.removeMouseActionListener(state, name);
    }
  }

  static removeMouseActionListener(
    state: MouseActionState,
    name: string
  ): void {
    const listener = state.listeners[name];
    document.removeEventListener(name, listener);
    delete state.listeners[name];
  }

  updateIframesPointerEvents(): void {
    const allow = !this.dragState.active && !this.resizeState.active;

    // TODO: Find a better way as this works on global state.
    for (const iframe of document.getElementsByTagName("iframe")) {
      iframe.style.pointerEvents = allow ? "auto" : "none";
    }
  }

  updatePosition(x: number, y: number, offsetX: number, offsetY: number): void {
    const iframeContainer = this.$refs["stream-container"];
    const rect = iframeContainer.getBoundingClientRect();

    const maxBottom = window.innerHeight - DRAGGING_MARGIN_Y;
    const maxRight = window.innerWidth - DRAGGING_MARGIN_X;

    const maxTop = maxBottom - rect.height;
    const maxLeft = maxRight - rect.width;

    this.x = Math.max(DRAGGING_MARGIN_X, Math.min(x - offsetX, maxLeft));
    this.y = Math.max(DRAGGING_MARGIN_Y, Math.min(y - offsetY, maxTop));
  }

  updateSize(direction: ResizeDirection, x: number, y: number): void {
    const iframeContainer = this.$refs["iframe-container"];
    const rect = iframeContainer.getBoundingClientRect();

    const minLeft = DRAGGING_MARGIN_X;
    const maxBottom = window.innerHeight - DRAGGING_MARGIN_Y;
    const maxRight = window.innerWidth - DRAGGING_MARGIN_X;

    const maxHeight = maxBottom - rect.top;
    const maxWidth = Math.min(maxRight - rect.left, maxHeight * ASPECT_RATIO);

    const calcResizeLeft = (): { width: number; x: number } => {
      const newX = Math.max(minLeft, x);
      const dx = this.x - newX;

      const newWidth = Math.max(MIN_WIDTH, this.width + dx);
      const dw = newWidth - this.width;

      return {
        width: newWidth,
        x: dw === dx ? newX : this.x - dw,
      };
    };

    const calcResizeRight = (): { width: number } => {
      return {
        width: Math.max(MIN_WIDTH, Math.min(x - this.x, maxWidth)),
      };
    };

    const calcResizeDown = (): { width: number; x: number } => {
      const height = Math.max(MIN_HEIGHT, Math.min(y - this.y, maxHeight));
      const width = Math.min(maxWidth, height * ASPECT_RATIO);
      const dw = width - this.width;
      return {
        width: width,
        x: Math.max(DRAGGING_MARGIN_X, this.x - dw / 2),
      };
    };

    switch (direction) {
      case ResizeDirection.WEST: {
        const { width, x } = calcResizeLeft();
        this.width = width;
        this.x = x;
        return;
      }

      case ResizeDirection.EAST: {
        this.width = calcResizeRight().width;
        return;
      }

      case ResizeDirection.SOUTH: {
        const { width, x } = calcResizeDown();
        this.width = width;
        this.x = x;
        return;
      }

      case ResizeDirection.SOUTH_WEST: {
        const { width: widthLeft, x: xLeft } = calcResizeLeft();
        const { width: widthDown } = calcResizeDown();

        if (widthLeft >= widthDown) {
          this.width = widthLeft;
          this.x = xLeft;
        } else {
          const dw = this.width - widthDown;
          if (this.x + dw < DRAGGING_MARGIN_X) {
            const dx = this.x - DRAGGING_MARGIN_X;
            this.width = this.width + dx;
            this.x = DRAGGING_MARGIN_X;
          } else {
            this.width = widthDown;
            this.x = this.x + dw;
          }
        }
        return;
      }

      case ResizeDirection.SOUTH_EAST: {
        const { width: widthRight } = calcResizeRight();
        const { width: widthDown } = calcResizeDown();

        this.width = Math.max(widthRight, widthDown);
        return;
      }
    }

    function checkExhaustive(direction: never): void {
      throw new Error(
        "Unhandled direction (add to switch above): " + direction
      );
    }

    checkExhaustive(direction);
  }
}
